<script setup>
import HeaderTitle from '@/components/layout/HeaderTitle';
import ReportService from '@/api/services/report.dailyproduction.service';
import ProjectsService from '@/api/services/projects.service';
import TrackerProjectInfo from '@/components/shared/TrackerProjectInfo.vue';
import Notification from '@/utils/plugins/NotificationService';
import defaultUserImage from '@/assets/images/user.png';
import { filterByNormalizedText } from '@/utils/textSearch';
import { Close } from '@element-plus/icons-vue';
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { useUiStore } from "@/app/stores/uiStore";

const uiStore = useUiStore();
const baseUri = process.env.VUE_APP_BASE_URI;
const baseUrl = baseUri?.replace(/\/$/, '') || '';
const storageKey = 'production-project-dashboard.ids';
const selectedProjectsStorageKey = 'production-project-dashboard.projects';
const now = new Date();

const selectedProjectIds = ref([]);
const selectedProjects = ref([]);
const dashboardProjects = ref([]);
const projects = ref([]);
const detailVisible = ref(false);
const selectedProject = ref(null);
const dropdownProject = ref(false);
const refSearchProject = ref(null);
const projectSearch = ref('');
const dashboardSearch = ref('');
const visibleProjects = computed(() => filterByNormalizedText(
  projects.value, dashboardSearch.value.trim(), project => project.name,
));
const selectorClients = ref([]);
const selectorLoading = ref(false);
const selectorSearchTimeout = ref(null);
let projectSelectorLoadPromise = null;
const selectedDatePreset = ref('this_week');
const selectedDateRange = ref([]);
const selectedProjectType = ref('');
const projectTypeOptions = ref([]);
const fromDate = ref(null);
const toDate = ref(null);
const kpis = ref({
  trackedProjects: 0,
  totalWorkedSeconds: 0,
  totalWorkedAllTimeSeconds: 0,
  totalEstimatedSeconds: 0,
  effectiveBalanceSeconds: 0,
  averageConsumption: 0,
  lastWeekSeconds: 0,
  peopleCount: 0,
});
const dashboardKpis = ref({ ...kpis.value });

const datePresetOptions = [
  { label: 'Hoy', value: 'today' },
  { label: 'Ayer', value: 'yesterday' },
  { label: 'Esta semana', value: 'this_week' },
  { label: 'Semana pasada', value: 'last_week' },
  { label: 'Este mes', value: 'this_month' },
  { label: 'El mes pasado', value: 'last_month' },
  { label: 'Todo', value: 'all' },
  { label: 'Personalizado', value: 'custom' },
];
const datePickerOptions = {
  firstDayOfWeek: 1,
};
const datePickerShortcuts = [
  { text: 'Hoy', value: () => getPresetRange('today') },
  { text: 'Ayer', value: () => getPresetRange('yesterday') },
  { text: 'Esta semana', value: () => getPresetRange('this_week') },
  { text: 'Semana pasada', value: () => getPresetRange('last_week') },
  { text: 'Este mes', value: () => getPresetRange('this_month') },
  { text: 'El mes pasado', value: () => getPresetRange('last_month') },
  { text: 'Todo', value: () => getPresetRange('all') },
];

const detailTitle = computed(() => selectedProject.value ? `Detalle de proyecto - ${selectedProject.value.name}` : 'Detalle de proyecto');
const formattedRange = computed(() => `${formatDateForView(fromDate.value)} - ${formatDateForView(toDate.value)}`);

onMounted(async () => {
  applyDatePreset(selectedDatePreset.value);
  await loadProjectTypes();
  await loadSavedProjectSelection();
  await loadProjectSelector();
  await loadDashboard();
});

onBeforeUnmount(() => {
  clearTimeout(selectorSearchTimeout.value);
});

const loadDashboard = async () => {
  if (!fromDate.value || !toDate.value) return;

  uiStore.startLoading();
  try {
    const response = await ReportService.getProjectDashboard({
      fromDate: formatDateForApi(fromDate.value),
      toDate: formatDateForApi(toDate.value),
    });
    dashboardProjects.value = response?.data?.projects ?? [];
    dashboardKpis.value = response?.data?.kpis ?? kpis.value;
    projects.value = filterProjectsByType(dashboardProjects.value);
    kpis.value = selectedProjectType.value
      ? buildFilteredKpis(projects.value)
      : dashboardKpis.value;
  } catch (error) {
    Notification.addNotification('Error al obtener el dashboard de proyectos.', 'error', 6000);
  } finally {
    uiStore.stopLoading();
  }
};

const handleDatePresetChange = (preset) => {
  applyDatePreset(preset);
  loadDashboard();
};

const refreshDashboard = async () => {
  await loadDashboard();
};

const loadProjectTypes = async () => {
  uiStore.startLoading();
  try {
    const response = await ProjectsService.getTypes();
    projectTypeOptions.value = (response?.data?.['hydra:member'] ?? [])
      .filter(type => normalizeProjectName(type.name) !== 'blh');
  } catch (error) {
    Notification.addNotification('Error al obtener los tipos de proyecto.', 'error', 6000);
  } finally {
    uiStore.stopLoading();
  }
};

const handleProjectTypeChange = () => {
  closeDetails();
  projects.value = filterProjectsByType(dashboardProjects.value);
  kpis.value = selectedProjectType.value
    ? buildFilteredKpis(projects.value)
    : dashboardKpis.value;
};

const getSelectedProjectTypeName = () => String(selectedProjectType.value?.name || selectedProjectType.value || '').trim();

const filterProjectsByType = (dashboardProjects) => {
  const selectedTypeName = normalizeProjectName(getSelectedProjectTypeName());
  if (!selectedTypeName) return dashboardProjects;

  return dashboardProjects.filter(project => projectMatchesSelectedType(project, selectedTypeName));
};

const projectMatchesSelectedType = (project, selectedTypeName) => {
  const projectTypeName = normalizeProjectName(getProjectTypeName(project));
  if (projectTypeName) {
    return projectTypeName === selectedTypeName;
  }

  const projectName = normalizeProjectName(project.name);

  if (selectedTypeName === 'bh') {
    return projectName.includes('bh');
  }

  if (selectedTypeName === 'fee') {
    return projectName.includes('fee');
  }

  if (selectedTypeName === 'normal') {
    return !projectName.includes('bh') && !projectName.includes('fee');
  }

  return projectName.includes(selectedTypeName);
};

const normalizeProjectName = (value) => String(value || '').trim().toLowerCase();

const getProjectTypeName = (project) => {
  return project?.projectType?.name
    ?? project?.type?.name
    ?? project?.projectTypeName
    ?? project?.typeName
    ?? project?.project_type?.name
    ?? '';
};

const buildFilteredKpis = (visibleProjects) => {
  const peopleIds = new Set();

  visibleProjects.forEach(project => {
    project.detail?.people?.forEach(person => peopleIds.add(person.id ?? person.name));
  });

  return {
    trackedProjects: visibleProjects.length,
    totalWorkedSeconds: sumProjectField(visibleProjects, 'workedSeconds'),
    totalWorkedAllTimeSeconds: sumProjectField(visibleProjects, 'totalWorkedSeconds'),
    totalEstimatedSeconds: sumProjectField(visibleProjects, 'estimatedSeconds'),
    effectiveBalanceSeconds: sumProjectField(visibleProjects, 'effectiveBalanceSeconds'),
    averageConsumption: visibleProjects.length
      ? Math.round(visibleProjects.reduce((total, project) => total + Number(project.consumptionPercent || 0), 0) / visibleProjects.length * 10) / 10
      : 0,
    lastWeekSeconds: sumProjectField(visibleProjects, 'lastWeekSeconds'),
    peopleCount: peopleIds.size || visibleProjects.reduce((total, project) => total + Number(project.detail?.peopleCount || 0), 0),
  };
};

const sumProjectField = (dashboardProjects, field) => dashboardProjects
  .reduce((total, project) => total + Number(project[field] || 0), 0);

const manejarCambioFecha = (val) => {
  if (!val || val.length !== 2) {
    applyDatePreset(selectedDatePreset.value === 'custom' ? 'today' : selectedDatePreset.value);
    if (selectedDatePreset.value === 'custom') {
      selectedDatePreset.value = 'today';
    }
  } else {
    selectedDateRange.value = val;
    selectedDatePreset.value = detectPresetFromRange(val) ?? 'custom';
    syncSelectedRange();
  }
  loadDashboard();
};

const applyDatePreset = (preset) => {
  selectedDateRange.value = getPresetRange(preset);
  syncSelectedRange();
};

const syncSelectedRange = () => {
  const [start, end] = selectedDateRange.value ?? [];
  fromDate.value = start ? startOfDay(start) : null;
  toDate.value = end ? startOfDay(end) : null;
};

const getPresetRange = (preset) => {
  if (preset === 'yesterday') {
    const yesterday = addDays(startOfDay(now), -1);
    return [yesterday, endOfDay(yesterday)];
  }

  if (preset === 'this_week') {
    const start = startOfWeek(now);
    return [start, endOfDay(now)];
  }

  if (preset === 'last_week') {
    const start = addDays(startOfWeek(now), -7);
    const end = addDays(start, 6);
    return [start, endOfDay(end)];
  }

  if (preset === 'this_month') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
    return [start, endOfDay(now)];
  }

  if (preset === 'last_month') {
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
    const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
    return [start, end];
  }

  if (preset === 'all') {
    const start = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate(), 0, 0, 0, 0);
    return [start, endOfDay(now)];
  }

  if (preset === 'custom') {
    return selectedDateRange.value?.length === 2 ? selectedDateRange.value : [startOfDay(now), endOfDay(now)];
  }

  return [startOfDay(now), endOfDay(now)];
};

const detectPresetFromRange = (range) => {
  const normalizedRange = normalizeRange(range);
  return datePresetOptions
    .filter(option => option.value !== 'custom')
    .find(option => compareRanges(normalizedRange, normalizeRange(getPresetRange(option.value))))
    ?.value ?? null;
};

const normalizeRange = (range) => [
  formatDateForApi(startOfDay(range[0])),
  formatDateForApi(endOfDay(range[1])),
];

const compareRanges = (firstRange, secondRange) => firstRange[0] === secondRange[0] && firstRange[1] === secondRange[1];

const toggleProject = async (project) => {
  const exists = selectedProjectIds.value.includes(project.id);
  if (exists) {
    await removeProject(project.id);
  } else {
    await addProject(project);
  }
  dropdownProject.value = false;
};

const loadProjectSelectorDebounced = () => {
  clearTimeout(selectorSearchTimeout.value);
  selectorSearchTimeout.value = setTimeout(loadProjectSelector, 180);
};

const loadProjectSelector = async () => {
  selectorLoading.value = true;
  uiStore.startLoading();
  try {
    const response = await ReportService.getProjectSelector({
      search: projectSearch.value,
      limit: 500,
    });
    selectorClients.value = response?.data?.['hydra:member'] ?? [];
  } catch (error) {
    Notification.addNotification('Error al cargar los proyectos del selector.', 'error', 6000);
  } finally {
    selectorLoading.value = false;
    uiStore.stopLoading();
  }
};

const removeProject = async (projectId) => {
  const projectToRemove = selectedProjects.value.find(project => project.id === projectId)
    || projects.value.find(project => project.id === projectId);

  uiStore.startLoading();
  try {
    await ReportService.deleteProjectDashboardProject(projectId);

    selectedProjectIds.value = selectedProjectIds.value.filter(id => id !== projectId);
    selectedProjects.value = selectedProjects.value.filter(project => project.id !== projectId);
    persistSelection();
    await loadDashboard();
    Notification.addNotification(
      projectToRemove?.name
        ? `Proyecto "${projectToRemove.name}" eliminado del dashboard correctamente.`
        : 'Proyecto eliminado del dashboard correctamente.',
      'success',
      6000,
    );
  } catch (error) {
    Notification.addNotification('Error al quitar el proyecto del panel.', 'error', 6000);
  } finally {
    uiStore.stopLoading();
  }
};

const addProject = async (project) => {
  uiStore.startLoading();
  try {
    const response = await ReportService.addProjectDashboardProject(project.id);
    const savedProject = response?.data?.project ?? project;
    selectedProjects.value = [
      ...selectedProjects.value.filter(item => item.id !== savedProject.id),
      savedProject,
    ];
    selectedProjectIds.value = selectedProjects.value.map(item => item.id);
    persistSelection();
    await loadDashboard();
    Notification.addNotification(
      savedProject?.name
        ? `Proyecto "${savedProject.name}" añadido al dashboard correctamente.`
        : 'Proyecto añadido al dashboard correctamente.',
      'success',
      6000,
    );
  } catch (error) {
    Notification.addNotification('Error al añadir el proyecto al panel.', 'error', 6000);
  } finally {
    uiStore.stopLoading();
  }
};

const loadSavedProjectSelection = async () => {
  uiStore.startLoading();
  try {
    const response = await ReportService.getProjectDashboardProjects();
    const serverProjects = response?.data?.projects ?? [];
    const localProjects = JSON.parse(localStorage.getItem(selectedProjectsStorageKey) || '[]');
    const localProjectIds = JSON.parse(localStorage.getItem(storageKey) || '[]');

    if (serverProjects.length < 1 && localProjectIds.length > 0) {
      const migrationResponse = await ReportService.replaceProjectDashboardProjects(localProjectIds);
      selectedProjects.value = migrationResponse?.data?.projects ?? localProjects;
    } else {
      selectedProjects.value = serverProjects;
    }

    selectedProjectIds.value = selectedProjects.value.map(item => item.id);
    persistSelection();
  } catch (error) {
    selectedProjectIds.value = JSON.parse(localStorage.getItem(storageKey) || '[]');
    selectedProjects.value = JSON.parse(localStorage.getItem(selectedProjectsStorageKey) || '[]');
    Notification.addNotification('Error al cargar los proyectos guardados del panel.', 'error', 6000);
  } finally {
    uiStore.stopLoading();
  }
};

const handleProjectPopoverBeforeEnter = () => {
  projectSelectorLoadPromise = loadProjectSelector();
};

const handleProjectPopoverAfterEnter = async () => {
  await projectSelectorLoadPromise;

  if (!dropdownProject.value) return;

  await nextTick();
  refSearchProject.value?.focus();
};

const isSelectedProject = (project) => selectedProjectIds.value.includes(project.id);

const getSelectedProjectStyle = (project) => {
  return isSelectedProject(project) ? { fontWeight: 700 } : {};
};

const openDetails = (project) => {
  selectedProject.value = project;
  detailVisible.value = true;
};

const closeDetails = () => {
  detailVisible.value = false;
};

const persistSelection = () => {
  localStorage.setItem(storageKey, JSON.stringify(selectedProjectIds.value));
  localStorage.setItem(selectedProjectsStorageKey, JSON.stringify(selectedProjects.value));
};

const formatIntegerWithThousands = (value) => String(value).replace(/\B(?=(\d{3})+(?!\d))/g, '.');

const formatDuration = (seconds) => {
  const absoluteSeconds = Math.abs(Math.round(seconds || 0));
  const hours = Math.floor(absoluteSeconds / 3600);
  const minutes = Math.floor((absoluteSeconds % 3600) / 60);
  return `${formatIntegerWithThousands(hours)}h ${String(minutes).padStart(2, '0')}m`;
};

const formatSignedDuration = (seconds) => {
  if (!seconds) return '0h 00m';
  return `${seconds < 0 ? '-' : '+'}${formatDuration(seconds)}`;
};

const balanceClass = (seconds) => seconds < 0 ? 'negative-balance' : 'positive-balance';

const consumptionBarClass = (percent) => {
  if (percent < 60) return 'consumption-bar-low';
  if (percent <= 80) return 'consumption-bar-medium';
  return 'consumption-bar-high';
};

const personPhoto = (person) => {
  const photo = person?.photo;
  if (!photo) return defaultUserImage;

  if (typeof photo === 'object' && photo.contentUrl) {
    return `${baseUrl}${photo.contentUrl}`;
  }

  if (typeof photo === 'string' && /^https?:\/\//.test(photo)) {
    return photo;
  }

  if (typeof photo === 'string' && photo.startsWith('/')) {
    return `${baseUrl}${photo}`;
  }

  return `${baseUrl}/images/employees/${photo}`;
};

const showDefaultPersonPhoto = (event) => {
  event.target.onerror = null;
  event.target.src = defaultUserImage;
};

const startOfDay = (date) => {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(0, 0, 0, 0);
  return normalizedDate;
};

const endOfDay = (date) => {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(23, 59, 59, 999);
  return normalizedDate;
};

const startOfWeek = (date) => {
  const day = date.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  return addDays(startOfDay(date), diffToMonday);
};

const addDays = (date, days) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
};

const formatDateForApi = (date) => {
  const normalizedDate = new Date(date);
  return `${normalizedDate.getFullYear()}-${String(normalizedDate.getMonth() + 1).padStart(2, '0')}-${String(normalizedDate.getDate()).padStart(2, '0')}`;
};

const formatDateForView = (date) => {
  if (!date) return '-';
  const normalizedDate = new Date(date);
  return `${String(normalizedDate.getDate()).padStart(2, '0')}/${String(normalizedDate.getMonth() + 1).padStart(2, '0')}/${normalizedDate.getFullYear()}`;
};
</script>

<template>
  <div class="project-dashboard-page">
    <header-title title="Producción" />

    <div class="project-dashboard-heading">
      <div>
        <h2 class="title-text">
          <span class="project-dashboard-heading-icon" aria-hidden="true">
            <font-awesome-icon icon="chart-bar" />
          </span>
          Dashboard de proyectos
        </h2>
        <p>Visión global del estado de tus proyectos y consumo de horas.</p>
      </div>
      <el-popover v-model:visible="dropdownProject" trigger="click" placement="bottom-end" :width="430"
        popper-class="project-search-popover" @before-enter="handleProjectPopoverBeforeEnter"
        @after-enter="handleProjectPopoverAfterEnter">
        <template #reference>
          <el-button type="primary" class="add-project-button">
            <font-awesome-icon icon="plus" />
            Proyecto
          </el-button>
        </template>
        <div class="optimized-project-search">
          <el-input ref="refSearchProject" v-model="projectSearch" placeholder="Busca un proyecto"
            class="projectInput mb-2" @input="loadProjectSelectorDebounced" />
          <el-scrollbar class="scroll">
            <div v-for="client in selectorClients" :key="client['@id'] || client.id">
              <span class="trackerClient">{{ client.name }}</span>
              <div v-for="projectOption in client.projectsNotArchived" :key="projectOption['@id']" class="row">
                <p :class="isSelectedProject(projectOption) ? 'col-11' : 'col-12'">
                  <tracker-project-info :project="projectOption" class="projectTracker pointer"
                    :style="getSelectedProjectStyle(projectOption)" @click="toggleProject(projectOption)" />
                </p>
                <p v-if="isSelectedProject(projectOption)"
                  class="col-1 d-flex justify-content-center align-content-end pointer">
                  <el-icon @click="toggleProject(projectOption)">
                    <Close />
                  </el-icon>
                </p>
              </div>
            </div>
            <div v-if="selectorClients.length < 1 && !selectorLoading">
              <span class="trackerClient empty">No se han encontrado proyectos</span>
            </div>
          </el-scrollbar>
        </div>
      </el-popover>
    </div>

    <section class="project-dashboard-filters">
      <div class="project-period-filter">
        <label class="label-state">Periodo:</label>
        <el-select class="w-100" v-model="selectedDatePreset" @change="handleDatePresetChange">
          <el-option v-for="option in datePresetOptions" :key="option.value" :label="option.label"
            :value="option.value" />
        </el-select>
      </div>
      <div class="project-type-filter">
        <label class="label-state">Tipo de proyecto:</label>
        <el-select class="w-100" v-model="selectedProjectType" placeholder="Todos" clearable filterable
          :value-key="'@id'" @change="handleProjectTypeChange">
          <el-option v-for="type in projectTypeOptions" :key="type.id" :label="type.name" :value="type" />
        </el-select>
      </div>
      <div v-if="selectedDatePreset === 'custom'" class="project-date-range-filter">
        <label>*Fechas:</label>
        <el-date-picker v-model="selectedDateRange" type="daterange" placeholder="Selecciona el periodo"
          start-placeholder="Fecha inicio" end-placeholder="Fecha fin" range-separator="-" unlink-panels
          format="DD/MM/YYYY" class="w-100 project-date-range" :picker-options="datePickerOptions"
          :shortcuts="datePickerShortcuts" @change="manejarCambioFecha" />
      </div>
      <div class="project-search-actions">
        <div class="project-name-filter">
          <label for="dashboard-project-search">Buscar proyecto:</label>
          <el-input id="dashboard-project-search" v-model="dashboardSearch" clearable class="w-100"
            placeholder="Buscar en tus proyectos" />
        </div>
        <div class="project-refresh-action">
          <label class="refresh-label-placeholder">Actualizar</label>
          <img @click="refreshDashboard" src="@/assets/icons/refresh-icon.png" alt="Actualizar" title="Actualizar"
            class="project-refresh-icon" />
        </div>
      </div>
    </section>

    <section class="project-dashboard-kpis">
      <article class="kpi-card violet">
        <div class="kpi-icon"><font-awesome-icon icon="briefcase" /></div>
        <div>
          <strong>{{ kpis.trackedProjects }}</strong>
          <span>proyectos seguidos</span>
          <small>Periodo seleccionado</small>
        </div>
      </article>
      <article class="kpi-card pink">
        <div class="kpi-icon"><font-awesome-icon icon="stopwatch" /></div>
        <div>
          <strong>{{ formatDuration(kpis.totalWorkedSeconds) }}</strong>
          <span>imputadas</span>
          <small>{{ kpis.peopleCount }} personas con horas</small>
        </div>
      </article>
      <article class="kpi-card cyan">
        <div class="kpi-icon"><font-awesome-icon icon="chart-bar" /></div>
        <div>
          <strong>{{ kpis.averageConsumption }}%</strong>
          <span>consumo medio</span>
          <small>{{ formatDuration(kpis.lastWeekSeconds) }} última semana</small>
        </div>
      </article>
      <article class="kpi-card amber">
        <div class="kpi-icon"><font-awesome-icon icon="thermometer-half" /></div>
        <div>
          <strong :class="balanceClass(kpis.effectiveBalanceSeconds)">
            {{ formatSignedDuration(kpis.effectiveBalanceSeconds) }}
          </strong>
          <span>saldo efectivo</span>
          <small>{{ formatDuration(kpis.totalEstimatedSeconds) }} estimadas</small>
        </div>
      </article>
    </section>

    <el-empty v-if="selectedProjectIds.length === 0" description="Añade proyectos para comenzar el seguimiento." />
    <el-empty v-else-if="projects.length === 0" description="No hay proyectos del tipo seleccionado en este periodo." />

    <el-empty v-else-if="visibleProjects.length === 0"
      description="No hay proyectos que coincidan con la búsqueda." />

    <section v-else class="project-card-grid">
      <article v-for="project in visibleProjects" :key="project.id" class="project-card"
        :style="{ '--accent': project.color }">
        <el-button class="remove-project-button" circle plain type="danger" @click="removeProject(project.id)">
          <font-awesome-icon icon="trash-alt" />
        </el-button>
        <h3>{{ project.name }}</h3>
        <dl>
          <div>
            <dt><font-awesome-icon icon="stopwatch" /> Horas en periodo</dt>
            <dd>{{ formatDuration(project.workedSeconds) }}</dd>
          </div>
          <div>
            <dt><font-awesome-icon icon="clock" /> Horas estimadas</dt>
            <dd>{{ formatDuration(project.estimatedSeconds) }}</dd>
          </div>
          <div>
            <dt><font-awesome-icon icon="stopwatch" /> Horas totales</dt>
            <dd>{{ formatDuration(project.totalWorkedSeconds) }}</dd>
          </div>
          <div>
            <dt><font-awesome-icon icon="thermometer-half" /> Saldo efectivo</dt>
            <dd :class="balanceClass(project.effectiveBalanceSeconds)">
              {{ formatSignedDuration(project.effectiveBalanceSeconds) }}
            </dd>
          </div>
          <div class="consumption-row">
            <dt><font-awesome-icon icon="chart-bar" /> % consumo</dt>
            <dd class="consumption-value">
              <span class="progress-row">
                <span :class="consumptionBarClass(project.consumptionPercent)"
                  :style="{ width: `${Math.min(project.consumptionPercent, 100)}%` }"></span>
              </span>
              <strong>{{ project.consumptionPercent }}%</strong>
            </dd>
          </div>
          <div>
            <dt><font-awesome-icon icon="calendar-alt" /> Última semana</dt>
            <dd>{{ formatDuration(project.lastWeekSeconds) }}</dd>
          </div>
        </dl>
        <div class="project-card-actions">
          <el-button type="primary" @click="openDetails(project)">
            <font-awesome-icon icon="eye" />
            Detalles
          </el-button>
        </div>
      </article>
    </section>

    <el-dialog v-model="detailVisible" width="920" class="project-detail-dialog" :center="true" :lock-scroll="true"
      :show-close="false" style="border-radius: 20px;" @closed="selectedProject = null">
      <div v-if="selectedProject" class="project-detail-dialog__content">
        <div class="project-detail-dialog__header">
          <div class="title-text project-detail-dialog__title">
            <font-awesome-icon icon="chart-bar" />
            {{ detailTitle }}
          </div>
          <img class="project-detail-dialog__close pointer" src="@/assets/images/aspa_blue_small_entrada.png" alt=""
            @click="closeDetails">
        </div>
        <div class="detail-header">
          <span>Total: {{ formatDuration(selectedProject.workedSeconds) }}</span>
          <small>{{ formattedRange }}</small>
        </div>
        <div class="detail-kpis">
          <div><strong>{{ selectedProject.detail.peopleCount }}</strong><span>Personas</span></div>
          <div><strong>{{ selectedProject.detail.tasksCount }}</strong><span>Tareas</span></div>
          <div><strong>{{ formatDuration(selectedProject.lastWeekSeconds) }}</strong><span>Última semana</span></div>
          <div>
            <strong :class="balanceClass(selectedProject.effectiveBalanceSeconds)">
              {{ formatSignedDuration(selectedProject.effectiveBalanceSeconds) }}
            </strong>
            <span>Saldo</span>
          </div>
        </div>
        <el-collapse>
          <el-collapse-item v-for="person in selectedProject.detail.people" :key="person.id" :name="`p-${person.id}`">
            <template #title>
              <div class="person-title">
                <img class="person-avatar" :src="personPhoto(person)" :alt="person.name"
                  @error="showDefaultPersonPhoto" />
                <strong>{{ person.name }}</strong>
                <em>{{ formatDuration(person.totalSeconds) }} · {{ person.percent }}%</em>
              </div>
            </template>
            <el-collapse class="task-collapse">
              <el-collapse-item v-for="task in person.tasks" :key="task.description" :name="task.description">
                <template #title>
                  <div class="task-title">
                    <span>{{ task.description }}</span>
                    <em>{{ formatDuration(task.totalSeconds) }}</em>
                  </div>
                </template>
                <el-table :data="task.entries" size="small" class="entry-table">
                  <el-table-column prop="date" label="Fecha" width="115" />
                  <el-table-column prop="description" label="Descripción" min-width="220" />
                  <el-table-column prop="start" label="Inicio" width="90" />
                  <el-table-column prop="end" label="Fin" width="90">
                    <template #default="{ row }">{{ row.end || '-' }}</template>
                  </el-table-column>
                  <el-table-column label="Horas" width="100">
                    <template #default="{ row }">{{ formatDuration(row.durationSeconds) }}</template>
                  </el-table-column>
                </el-table>
              </el-collapse-item>
            </el-collapse>
          </el-collapse-item>
        </el-collapse>
        <div class="project-detail-dialog__actions">
          <button type="button" class="btn btn_yellow project-detail-dialog__button" @click="closeDetails">
            Cerrar
          </button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<style scoped>
.project-dashboard-page {
  padding-bottom: 32px;
}

:deep(#header-title) {
  margin-bottom: 0;
}

.project-dashboard-heading,
.project-dashboard-filters,
.project-dashboard-kpis,
.project-card-grid {
  max-width: 1680px;
  margin: 0 auto;
}

.project-dashboard-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-top: 22px;
  margin-bottom: 16px;
}

.project-dashboard-heading h2 {
  display: flex;
  align-items: center;
  gap: 5px;
  margin: 0;
  margin-left: 0 !important;
  text-align: left;
}

.project-dashboard-heading-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  font-size: 24px;
  letter-spacing: normal;
}

.project-dashboard-heading p {
  margin: 4px 0 0;
  color: #667085;
}

.add-project-button {
  min-width: 150px;
  font-weight: 700;
}

:global(.project-search-popover) {
  padding: 10px;
}

.optimized-project-search {
  width: 100%;
  height: 330px;
  overflow: hidden;
}

.scroll {
  height: 280px;
  width: 100%;
  margin-top: 10px;
}

.projectInput {
  width: 100%;
}

.projectTracker {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.trackerClient {
  color: #474CE6;
  display: block;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 3px;
}

.trackerClient.empty {
  color: #000;
}

.project-dashboard-filters {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 18px;
  flex-wrap: wrap;
  padding: 18px;
  background: #fff;
  border: 1px solid #e5e7ef;
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(31, 41, 55, 0.08);
  overflow: hidden;
}

.project-dashboard-filters::before {
  content: "";
  position: absolute;
  inset: 0;
  background:
    linear-gradient(90deg, rgba(255, 255, 255, 0.96) 0%, rgba(255, 255, 255, 0.9) 32%, rgba(255, 255, 255, 0.62) 58%, rgba(255, 255, 255, 0.18) 100%),
    url('@/assets/images/production/projects-dashboard-bg.png') center 48% / cover no-repeat;
  opacity: 0.96;
  pointer-events: none;
}

.project-dashboard-filters>* {
  position: relative;
  z-index: 1;
}

.project-period-filter,
.project-type-filter,
.project-name-filter,
.project-date-range-filter {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.project-period-filter,
.project-type-filter {
  width: 260px;
}

.project-date-range-filter {
  width: min(100%, 520px);
}

.project-period-filter label,
.project-type-filter label,
.project-date-range-filter label,
.project-name-filter label {
  margin: 0;
  font-weight: 600;
  color: #4b5563;
}

.project-search-actions {
  display: flex;
  align-items: flex-start;
  gap: 18px;
  flex: 0 1 360px;
  min-width: 0;
  max-width: 100%;
}

.project-name-filter {
  flex: 1 1 auto;
  min-width: 0;
}

.project-refresh-action {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.refresh-label-placeholder {
  margin: 0;
  font-weight: 600;
  color: transparent;
  user-select: none;
}

.project-refresh-icon {
  flex: 0 0 auto;
  width: 35px;
  height: 35px;
  padding: 5px 3px 5px 5px;
  border-radius: 50%;
  background: #F7D20E;
  cursor: pointer;
  transition: transform 0.16s ease, box-shadow 0.16s ease;
}

.project-refresh-icon:hover {
  box-shadow: 0 5px 12px rgba(247, 210, 14, 0.36);
  transform: rotate(18deg);
}

.project-date-range-filter :deep(.el-input__wrapper) {
  padding-left: 14px;
  border-radius: 999px;
}

.project-date-range-filter :deep(.el-input__prefix) {
  margin-right: 8px;
}

.project-date-range-filter :deep(.el-input__prefix-inner) {
  padding-left: 4px;
}

.project-dashboard-kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-top: 18px;
  margin-bottom: 18px;
}

.kpi-card {
  display: flex;
  align-items: center;
  gap: 18px;
  min-height: 116px;
  padding: 18px;
  background: #fff;
  border: 1px solid #e5e7ef;
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(31, 41, 55, 0.08);
}

.kpi-icon {
  display: grid;
  place-items: center;
  width: 70px;
  height: 70px;
  border-radius: 8px;
  color: #fff;
  font-size: 30px;
}

.kpi-card.violet .kpi-icon {
  background: #4f46e5;
}

.kpi-card.pink .kpi-icon {
  background: #f72563;
}

.kpi-card.cyan .kpi-icon {
  background: #12bfc5;
}

.kpi-card.amber .kpi-icon {
  background: #f4b400;
}

.kpi-card strong {
  display: block;
  font-size: 28px;
  line-height: 1.1;
  color: #3445f4;
}

.kpi-card span,
.kpi-card small {
  display: block;
  color: #4b5563;
}

.project-card-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

.project-card {
  position: relative;
  padding: 18px;
  background: #fff;
  border: 1px solid #e5e7ef;
  border-top: 4px solid var(--accent);
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(31, 41, 55, 0.08);
}

.project-card h3 {
  margin: 0 38px 16px 0;
  color: #3445f4;
  font-size: 19px;
  font-weight: 700;
}

.remove-project-button {
  position: absolute;
  top: 14px;
  right: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  min-height: 32px;
  padding: 0;
  border-color: #ffd4da;
  background: #fff7f8;
  color: #f4213d;
  box-shadow: 0 4px 10px rgba(244, 33, 61, 0.12);
}

.remove-project-button:hover,
.remove-project-button:focus {
  border-color: #f4213d;
  background: #f4213d;
  color: #fff;
}

.project-card dl,
.project-card dd {
  margin: 0;
}

.project-card dl>div:not(.progress-row) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}

.project-card dt {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #4b5563;
  font-size: 14px;
  font-weight: 500;
}

.project-card dd {
  flex: 0 0 auto;
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
}

.consumption-row .consumption-value {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 1 1 auto;
  gap: 10px;
  min-width: 0;
}

.consumption-value strong {
  min-width: 36px;
  text-align: right;
}

.progress-row {
  display: block;
  flex: 1 1 112px;
  max-width: 135px;
  height: 8px;
  background: #d9dde5;
  border-radius: 999px;
  overflow: hidden;
}

.progress-row span {
  display: block;
  height: 100%;
}

.progress-row .consumption-bar-low {
  background: linear-gradient(90deg, #008a22, #08a536);
}

.progress-row .consumption-bar-medium {
  background: linear-gradient(90deg, #f59e0b, #f97316);
}

.progress-row .consumption-bar-high {
  background: linear-gradient(90deg, #dc2626, #ef4444);
}

.project-card-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
}

.positive-balance {
  color: #008a22 !important;
}

.negative-balance {
  color: #f4213d !important;
}

.project-detail-dialog__content {
  padding-bottom: 8px;
}

.project-detail-dialog__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 18px;
}

.project-detail-dialog__title {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #474CE6;
  line-height: 1.15;
}

.project-detail-dialog__close {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.detail-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  margin: 0 0 18px;
}

.detail-header span {
  padding: 6px 14px;
  color: #3445f4;
  background: #eceeff;
  border-radius: 6px;
  font-weight: 700;
}

.detail-kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.detail-kpis div {
  padding: 14px;
  border: 1px solid #e5e7ef;
  border-radius: 8px;
}

.detail-kpis strong,
.detail-kpis span {
  display: block;
}

.person-title,
.task-title {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 12px;
}

.person-avatar {
  width: 36px;
  height: 36px;
  object-fit: cover;
  border-radius: 50%;
  background: #e8ebff;
  margin-left: 10px;
}

.person-title em,
.task-title em {
  margin-left: auto;
  color: #1f2937;
  font-style: normal;
  font-weight: 700;
}

.task-collapse {
  padding-left: 22px;
}

.entry-table {
  width: 100%;
}

.project-detail-dialog__actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
}

.project-detail-dialog__button {
  width: auto;
  min-width: 96px;
  padding-left: 22px;
  padding-right: 22px;
}

@media (max-width: 1200px) {

  .project-dashboard-kpis,
  .project-card-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {

  .project-dashboard-heading,
  .project-dashboard-filters {
    align-items: stretch;
    flex-direction: column;
  }

  .project-dashboard-kpis,
  .project-card-grid,
  .detail-kpis {
    grid-template-columns: 1fr;
  }

  .project-period-filter,
  .project-type-filter,
  .project-date-range-filter,
  .project-date-range-filter :deep(.el-date-editor) {
    width: 100%;
  }

  .project-search-actions {
    flex: 0 0 auto;
    width: 100%;
  }

  .progress-row {
    max-width: none;
  }

  .project-detail-dialog__header,
  .detail-header,
  .project-detail-dialog__actions {
    align-items: stretch;
    flex-direction: column;
  }

  .project-detail-dialog__title {
    font-size: 28px;
  }

  .project-detail-dialog__close {
    align-self: flex-end;
    order: -1;
  }

  .project-detail-dialog__button {
    width: 100%;
  }
}
</style>
